import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactFlow, {
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    type Node,
    type Edge,
    BackgroundVariant,
    ConnectionLineType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Globe,
    Search,
    Maximize2,
    ZoomIn,
    ZoomOut,
    X,
    Server,
    Mail,
    Briefcase,
    Building2,
    Users,
} from 'lucide-react';
import ADNode from './ADNode';
import ADEdge from './ADEdge';
import type { ADDomain, ADOU, ADUser, ADComputer, ADGroup } from './adTypes';
import { CustomDropdown } from '../../components/CustomDropdown ';

const nodeTypes = { adNode: ADNode };
const edgeTypes = { adEdge: ADEdge };

interface ADTopologyFlowProps {
    domain: ADDomain | null;
    ous: ADOU[];
    users: ADUser[];
    computers: ADComputer[];
    groups: ADGroup[];
    loading?: boolean;
}

export default function ADTopologyFlow({
    domain,
    ous,
    users,
    computers,
    groups,
    loading = false,
}: ADTopologyFlowProps) {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNode, setSelectedNode] = useState<any | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<string>('all');

    // Set of node IDs that are expanded. Domain Root ('domain-root') is expanded by default to show 1st level children.
    const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set(['domain-root']));
    const reactFlowInstanceRef = useRef<any>(null);

    const toggleNodeExpand = useCallback((nodeId: string) => {
        setExpandedNodeIds(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    }, []);

    // Build Graph Hierarchy: Show 1st level children first, expand 2nd level on click
    useEffect(() => {
        const newNodes: Node[] = [];
        const newEdges: Edge[] = [];

        // Check if domain or objects exist
        if (!domain && ous.length === 0 && users.length === 0 && computers.length === 0 && groups.length === 0) {
            setNodes([]);
            setEdges([]);
            return;
        }

        const rootId = 'domain-root';
        const domainName = domain?.domain || 'Active Directory Domain';
        const ouList = ous.length > 0 ? ous : [];

        // 1. Map leaf objects by parent OU or domain root
        interface LeafItem {
            id: string;
            type: any;
            label: string;
            subLabel: string;
            parentId: string;
            raw: any;
            admin?: boolean;
            disabled?: boolean;
        }

        const allLeafObjects: LeafItem[] = [];

        // Map users
        users.forEach((u, i) => {
            const isAdmin = u.domain_admin || u.enterprise_admin;
            const isDisabled = u.account_disabled;
            const type = isAdmin ? 'user_admin' : isDisabled ? 'user_disabled' : 'user';

            let parentId = rootId;
            const parentOu = ouList.find(o => u.distinguished_name && u.distinguished_name.toLowerCase().includes(o.distinguished_name.toLowerCase()));
            if (parentOu) parentId = `ou-${parentOu.object_guid}`;

            allLeafObjects.push({
                id: `user-${u.object_guid || i}`,
                type,
                label: u.display_name || u.name,
                subLabel: u.user_principal_name || u.sam_account_name || u.title || 'User',
                parentId,
                raw: u,
                admin: isAdmin,
                disabled: isDisabled,
            });
        });

        // Map computers
        computers.forEach((c, i) => {
            let parentId = rootId;
            const parentOu = ouList.find(o => c.distinguished_name && c.distinguished_name.toLowerCase().includes(o.distinguished_name.toLowerCase()));
            if (parentOu) parentId = `ou-${parentOu.object_guid}`;

            allLeafObjects.push({
                id: `comp-${c.object_guid || i}`,
                type: 'computer',
                label: c.name,
                subLabel: c.dns_host_name || c.operating_system || 'Computer',
                parentId,
                raw: c,
                disabled: c.account_disabled,
            });
        });

        // Map groups
        groups.slice(0, 15).forEach((g, i) => {
            const isBuiltin = g.is_critical_system_object || (g.distinguished_name && g.distinguished_name.toLowerCase().includes('builtin'));
            let parentId = rootId;
            const parentOu = ouList.find(o => g.distinguished_name && g.distinguished_name.toLowerCase().includes(o.distinguished_name.toLowerCase()));
            if (parentOu) parentId = `ou-${parentOu.object_guid}`;

            allLeafObjects.push({
                id: `grp-${g.object_guid || i}`,
                type: isBuiltin ? 'group_builtin' : 'group',
                label: g.name,
                subLabel: `${g.scope || 'Global'} ${g.category || 'Security'}`,
                parentId,
                raw: g,
            });
        });

        // Group leaf objects by parentId
        const childrenByParent: Record<string, LeafItem[]> = {};
        allLeafObjects.forEach(item => {
            if (!childrenByParent[item.parentId]) childrenByParent[item.parentId] = [];
            childrenByParent[item.parentId].push(item);
        });

        // Layout Parameters
        const nodeWidth = 240;
        const hGap = 40;
        const vStep = 180;
        const itemSpan = nodeWidth + hGap;

        // Build Level 1 Column Data (OUs + Built-in System Container if root children exist)
        interface ColumnData {
            id: string;
            label: string;
            subLabel?: string;
            dn?: string;
            nodeType: string;
            width: number;
            children: LeafItem[];
            rawObject?: any;
        }

        const level1Columns: ColumnData[] = [];

        // Add OUs
        ouList.forEach((ou, idx) => {
            const ouId = `ou-${ou.object_guid || idx}`;
            const children = childrenByParent[ouId] || [];
            const isOuExpanded = expandedNodeIds.has(ouId);
            const childrenInRow = isOuExpanded ? Math.min(children.length || 1, 4) : 1;
            const w = Math.max(itemSpan, childrenInRow * itemSpan);
            const isDC = ou.name.toLowerCase().includes('domain controller');

            level1Columns.push({
                id: ouId,
                label: ou.name,
                subLabel: ou.distinguished_name,
                dn: ou.distinguished_name,
                nodeType: isDC ? 'dc' : 'ou',
                width: w,
                children,
                rawObject: ou,
            });
        });

        // Add Built-in / System Container for direct domain objects if any exist
        const rootChildren = childrenByParent[rootId] || [];
        if (rootChildren.length > 0) {
            const containerId = 'ou-root-container';
            const isContainerExpanded = expandedNodeIds.has(containerId);
            const childrenInRow = isContainerExpanded ? Math.min(rootChildren.length || 1, 4) : 1;
            const w = Math.max(itemSpan, childrenInRow * itemSpan);

            level1Columns.push({
                id: containerId,
                label: 'Built-in & System Objects',
                subLabel: 'CN=Users, CN=Computers, CN=Builtin',
                dn: domain?.distinguished_name ? `CN=System,${domain.distinguished_name}` : 'CN=Builtin',
                nodeType: 'container',
                width: w,
                children: rootChildren,
                rawObject: {
                    name: 'Built-in & System Objects',
                    distinguished_name: domain?.distinguished_name ? `CN=System,${domain.distinguished_name}` : 'CN=Builtin',
                    description: 'Container for direct domain objects, built-in users, computers, and groups.',
                },
            });
        }

        // Total Level 1 layout width
        const totalLevel1Width = level1Columns.reduce((acc, curr) => acc + curr.width, 0) + Math.max(0, level1Columns.length - 1) * hGap;
        const totalLayoutWidth = Math.max(itemSpan * 2, totalLevel1Width);

        // Domain Root Position (Level 0 - Centered at top)
        const isDomainExpanded = expandedNodeIds.has(rootId);
        const isRootSelected = selectedNode?.id === rootId;
        newNodes.push({
            id: rootId,
            type: 'adNode',
            position: { x: totalLayoutWidth / 2 - nodeWidth / 2, y: 0 },
            data: {
                nodeType: 'domain',
                label: domain?.dc || domainName.split('.')[0].toUpperCase(),
                subLabel: domainName,
                dn: domain?.distinguished_name,
                isPrimary: true,
                isSelected: isRootSelected,
                hasChildren: level1Columns.length > 0,
                childCount: level1Columns.length,
                isExpanded: isDomainExpanded,
                rawObject: domain,
                onToggleExpand: () => toggleNodeExpand(rootId),
            },
        });

        // 2. Position 1st Level Columns (OUs, DCs, System Containers) if Domain Root is expanded
        if (isDomainExpanded && level1Columns.length > 0) {
            let currentX = 0;
            level1Columns.forEach((colData) => {
                const colCenterX = currentX + colData.width / 2 - nodeWidth / 2;
                const colY = vStep;
                const isSelected = selectedNode?.id === colData.id;
                const isColExpanded = expandedNodeIds.has(colData.id);

                newNodes.push({
                    id: colData.id,
                    type: 'adNode',
                    position: { x: colCenterX, y: colY },
                    data: {
                        nodeType: colData.nodeType as any,
                        label: colData.label,
                        subLabel: colData.subLabel,
                        dn: colData.dn,
                        isSelected,
                        hasChildren: colData.children.length > 0,
                        childCount: colData.children.length,
                        isExpanded: isColExpanded,
                        rawObject: colData.rawObject,
                        onToggleExpand: () => toggleNodeExpand(colData.id),
                    },
                });

                // Edge Domain -> Column Node
                newEdges.push({
                    id: `edge-${rootId}-${colData.id}`,
                    source: rootId,
                    target: colData.id,
                    type: 'adEdge',
                    animated: true,
                    style: {
                        stroke: colData.nodeType === 'dc' ? '#2dd4bf' : colData.nodeType === 'container' ? '#94a3b8' : '#f59e0b',
                    },
                });

                // 3. Position 2nd Level Children under this Column ONLY IF Column is expanded!
                if (isColExpanded && colData.children.length > 0) {
                    const maxPerRow = 4;
                    colData.children.forEach((child, cIdx) => {
                        const row = Math.floor(cIdx / maxPerRow);
                        const col = cIdx % maxPerRow;
                        const totalInRow = Math.min(maxPerRow, colData.children.length - row * maxPerRow);

                        const cX = colCenterX + (col - (totalInRow - 1) / 2) * itemSpan;
                        const cY = colY + vStep + row * 90;
                        const isChildSelected = selectedNode?.id === child.id;

                        newNodes.push({
                            id: child.id,
                            type: 'adNode',
                            position: { x: cX, y: cY },
                            data: {
                                nodeType: child.type,
                                label: child.label,
                                subLabel: child.subLabel,
                                dn: child.raw.distinguished_name,
                                isSelected: isChildSelected,
                                disabled: child.disabled,
                                admin: child.admin,
                                rawObject: child.raw,
                            },
                        });

                        newEdges.push({
                            id: `edge-${colData.id}-${child.id}`,
                            source: colData.id,
                            target: child.id,
                            type: 'adEdge',
                            animated: child.type === 'user_admin',
                            style: {
                                stroke: child.type === 'user_admin' ? '#ef4444' : child.type === 'computer' ? '#a855f7' : child.type === 'group' ? '#6366f1' : '#10b981',
                            },
                        });
                    });
                }

                currentX += colData.width + hGap;
            });
        }

        setNodes(newNodes);
        setEdges(newEdges);
    }, [domain, ous, users, computers, groups, selectedNode, expandedNodeIds, toggleNodeExpand, setNodes, setEdges]);

    // Handle Search / Filter
    const filteredNodes = useMemo(() => {
        if (!searchTerm && filterType === 'all') return nodes;

        const term = searchTerm.toLowerCase();
        return nodes.map(node => {
            const matchesSearch = !term ||
                node.data.label.toLowerCase().includes(term) ||
                (node.data.subLabel && node.data.subLabel.toLowerCase().includes(term)) ||
                (node.data.dn && node.data.dn.toLowerCase().includes(term));

            const matchesFilter = filterType === 'all' ||
                (filterType === 'users' && (node.data.nodeType === 'user' || node.data.nodeType === 'user_admin' || node.data.nodeType === 'user_disabled')) ||
                (filterType === 'ous' && (node.data.nodeType === 'ou' || node.data.nodeType === 'dc' || node.data.nodeType === 'container')) ||
                (filterType === 'computers' && node.data.nodeType === 'computer') ||
                (filterType === 'groups' && (node.data.nodeType === 'group' || node.data.nodeType === 'group_builtin'));

            const isMatch = matchesSearch && matchesFilter;

            return {
                ...node,
                data: {
                    ...node.data,
                    isSelected: isMatch && (searchTerm !== '' || filterType !== 'all') ? true : node.data.isSelected,
                },
            };
        });
    }, [nodes, searchTerm, filterType]);

    // Auto-center canvas smoothly when nodes or expanded state updates
    useEffect(() => {
        if (reactFlowInstanceRef.current && nodes.length > 0) {
            const timer = setTimeout(() => {
                reactFlowInstanceRef.current.fitView({ padding: 0.35, duration: 400 });
            }, 60);
            return () => clearTimeout(timer);
        }
    }, [nodes]);

    // Handle Node Click: Select node in Inspector + Toggle Expand if it has children & center
    const onNodeClick = (_: React.MouseEvent, node: Node) => {
        setSelectedNode(node);
        if (node.data.hasChildren) {
            toggleNodeExpand(node.id);
        } else if (reactFlowInstanceRef.current) {
            reactFlowInstanceRef.current.setCenter(node.position.x, node.position.y, { zoom: 1, duration: 400 });
        }
    };

    const handleResetView = () => {
        if (reactFlowInstanceRef.current) {
            reactFlowInstanceRef.current.fitView({ padding: 0.35, duration: 400 });
        }
    };

    const handleZoomIn = () => {
        if (reactFlowInstanceRef.current) reactFlowInstanceRef.current.zoomIn({ duration: 300 });
    };

    const handleZoomOut = () => {
        if (reactFlowInstanceRef.current) reactFlowInstanceRef.current.zoomOut({ duration: 300 });
    };

    const selectedRaw = selectedNode?.data?.rawObject;
    const nodeType = selectedNode?.data?.nodeType;

    return (
        <div className="w-full h-[calc(100vh-12.5rem)] bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-xl text-slate-800 dark:text-white flex flex-row relative overflow-hidden shadow-sm dark:shadow-2xl">
            {/* Center Canvas Area */}
            <div className="flex-1 relative flex flex-col h-full bg-slate-50 dark:bg-gray-950 min-w-0">
                {/* Canvas Compact Toolbar */}
                <div className="flex items-center justify-between px-3 py-2 bg-white/90 dark:bg-gray-900/90 border-b border-slate-200 dark:border-gray-800 shrink-0 backdrop-blur-md z-10 gap-2">
                    {/* Search & Filter */}
                    <div className="flex items-center gap-2">
                        <div className="relative w-56 group">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search graph nodes..."
                                className="w-full bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-md pl-8 pr-3 py-1 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500/60 transition-all"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-gray-400 dark:hover:text-white"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>

                        <CustomDropdown
                            data={[
                                { label: 'All Types', value: 'all' },
                                { label: 'OUs & Containers', value: 'ous' },
                                { label: 'Users', value: 'users' },
                                { label: 'Computers', value: 'computers' },
                                { label: 'Groups', value: 'groups' },
                            ]}
                            selectedValue={filterType}
                            onSelect={(val) => setFilterType(val)}
                            width="w-44"
                            buttonHeight={8}
                            placeholder="Select Filter"
                        />
                    </div>

                    {/* View Controls */}
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            onClick={handleResetView}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-white hover:bg-slate-100 dark:bg-gray-900 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-800 text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white rounded-md transition-all"
                        >
                            <Maximize2 size={12} className="text-orange-500 dark:text-orange-400" />
                            Fit
                        </button>
                        <button
                            onClick={handleZoomIn}
                            className="p-1 bg-white hover:bg-slate-100 dark:bg-gray-900 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-800 text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white rounded-md transition-all"
                            title="Zoom In"
                        >
                            <ZoomIn size={13} />
                        </button>
                        <button
                            onClick={handleZoomOut}
                            className="p-1 bg-white hover:bg-slate-100 dark:bg-gray-900 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-800 text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white rounded-md transition-all"
                            title="Zoom Out"
                        >
                            <ZoomOut size={13} />
                        </button>
                    </div>
                </div>

                {/* ReactFlow Canvas */}
                <div className="flex-1 w-full h-full relative">
                    {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/90 z-20">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-7 h-7 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-xs font-mono text-slate-600 dark:text-gray-400">Loading AD Topology...</span>
                            </div>
                        </div>
                    ) : nodes.length === 0 ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 dark:bg-gray-900/90 text-slate-400 dark:text-gray-500 font-mono gap-2 z-20">
                            <Globe size={28} className="text-slate-300 dark:text-gray-600" />
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-gray-400">No Active Directory Topology Data</span>
                            <span className="text-[11px] text-slate-400 dark:text-gray-600">Initiate an AD scan to populate topology graph</span>
                        </div>
                    ) : null}

                    <ReactFlow
                        nodes={filteredNodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onNodeClick={onNodeClick}
                        onInit={(instance) => { reactFlowInstanceRef.current = instance; }}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        nodeOrigin={[0.5, 0.5]}
                        fitView
                        fitViewOptions={{ padding: 0.35 }}
                        minZoom={0.15}
                        maxZoom={1.8}
                        connectionLineType={ConnectionLineType.Bezier}
                        proOptions={{ hideAttribution: true }}
                        style={{ background: 'transparent' }}
                    >
                        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#94a3b8" className="opacity-40 dark:opacity-100 dark:!text-gray-700" />
                        <Controls className="!bg-white dark:!bg-gray-900 !border-slate-200 dark:!border-gray-800 !fill-slate-600 dark:!fill-gray-400 shadow-sm" position="bottom-left" />
                    </ReactFlow>

                    {/* Floating Compact Legend */}
                    <div className="absolute bottom-3 left-14 bg-white/95 dark:bg-gray-900/90 border border-slate-200 dark:border-gray-800 backdrop-blur-md rounded-lg px-3 py-1.5 shadow-md dark:shadow-xl z-10 hidden md:block">
                        <div className="flex items-center gap-3 text-[10px] text-slate-700 dark:text-gray-300 font-mono">
                            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Domain</div>
                            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-500 dark:bg-teal-400"></span> DC</div>
                            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 dark:bg-amber-400"></span> OU</div>
                            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-500 dark:bg-slate-400"></span> System Container</div>
                            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400"></span> User</div>
                            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> Admin</div>
                            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500 dark:bg-purple-400"></span> Computer</div>
                            <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500 dark:bg-indigo-400"></span> Group</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Compact Right Inspector Drawer */}
            {selectedNode && selectedRaw && (
                <div className="w-[310px] h-full flex flex-col bg-slate-50 dark:bg-gray-900 border-l border-slate-200 dark:border-gray-800 overflow-y-auto custom-scroll shadow-xl dark:shadow-2xl z-30 shrink-0">
                    <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/90 sticky top-0 backdrop-blur-md">
                        <div className="flex items-center gap-2 min-w-0">
                            <Server className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 shrink-0" />
                            <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider truncate">
                                Inspector
                            </h3>
                        </div>
                        <button
                            onClick={() => setSelectedNode(null)}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-gray-800 rounded text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                        >
                            <X size={13} />
                        </button>
                    </div>

                    <div className="p-3 space-y-3">
                        {/* Primary Box */}
                        <div className="bg-white dark:bg-gray-900/90 border border-slate-200 dark:border-gray-800 rounded-lg p-3 space-y-1.5 shadow-sm">
                            <div className="flex items-start justify-between gap-2">
                                <h4 className="text-xs font-bold text-slate-900 dark:text-white font-mono break-all leading-snug">
                                    {selectedNode.data.label}
                                </h4>
                                <span className="px-1.5 py-0.2 rounded text-[8px] font-bold uppercase border bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30 shrink-0">
                                    {nodeType}
                                </span>
                            </div>
                            {selectedNode.data.subLabel && (
                                <p className="text-[10px] text-slate-500 dark:text-gray-400 font-mono break-all">
                                    {selectedNode.data.subLabel}
                                </p>
                            )}
                        </div>

                        {/* Attribute Grid */}
                        <div className="space-y-2 text-xs font-mono">
                            <span className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest block border-b border-slate-200 dark:border-gray-800 pb-1">
                                Metadata
                            </span>

                            {selectedRaw.distinguished_name && (
                                <div>
                                    <span className="text-[8px] text-slate-500 dark:text-gray-500 uppercase block">Distinguished Name</span>
                                    <span className="text-slate-800 dark:text-gray-200 break-all text-[10px] block bg-white dark:bg-gray-900 p-1.5 rounded border border-slate-200 dark:border-gray-800 mt-0.5">
                                        {selectedRaw.distinguished_name}
                                    </span>
                                </div>
                            )}

                            {selectedRaw.object_sid && (
                                <div>
                                    <span className="text-[8px] text-slate-500 dark:text-gray-500 uppercase block">Object SID</span>
                                    <span className="text-purple-600 dark:text-purple-300 font-bold text-[10px] break-all">{selectedRaw.object_sid}</span>
                                </div>
                            )}

                            {selectedRaw.object_guid && (
                                <div>
                                    <span className="text-[8px] text-slate-500 dark:text-gray-500 uppercase block">Object GUID</span>
                                    <span className="text-cyan-600 dark:text-cyan-300 font-bold text-[10px]">{selectedRaw.object_guid}</span>
                                </div>
                            )}

                            {selectedRaw.mail && (
                                <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 dark:text-gray-400 flex items-center gap-1"><Mail size={11} className="text-blue-500 dark:text-blue-400" /> Email</span>
                                    <span className="text-slate-800 dark:text-gray-200 font-semibold">{selectedRaw.mail}</span>
                                </div>
                            )}

                            {selectedRaw.department && (
                                <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 dark:text-gray-400 flex items-center gap-1"><Building2 size={11} className="text-emerald-500 dark:text-emerald-400" /> Dept</span>
                                    <span className="text-slate-800 dark:text-gray-200 font-semibold">{selectedRaw.department}</span>
                                </div>
                            )}

                            {selectedRaw.title && (
                                <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-slate-500 dark:text-gray-400 flex items-center gap-1"><Briefcase size={11} className="text-amber-500 dark:text-amber-400" /> Title</span>
                                    <span className="text-slate-800 dark:text-gray-200 font-semibold">{selectedRaw.title}</span>
                                </div>
                            )}
                        </div>

                        {/* Member Of Groups */}
                        {(() => {
                            const memberOfList: string[] = Array.isArray(selectedRaw.member_of)
                                ? selectedRaw.member_of
                                : typeof selectedRaw.member_of === 'string'
                                ? [selectedRaw.member_of]
                                : [];
                            if (memberOfList.length === 0) return null;

                            return (
                                <div className="space-y-1.5 pt-2 border-t border-slate-200 dark:border-gray-800">
                                    <span className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest block">
                                        Member Of ({memberOfList.length})
                                    </span>
                                    <div className="space-y-1 max-h-40 overflow-y-auto custom-scroll pr-1">
                                        {memberOfList.map((groupDn: string, gIdx: number) => {
                                            const groupCn = typeof groupDn === 'string' ? groupDn.split(',')[0].replace(/^CN=/i, '') : String(groupDn);
                                            return (
                                                <div key={gIdx} className="bg-white dark:bg-gray-900 p-1.5 rounded border border-slate-200 dark:border-gray-800 text-[10px] text-indigo-600 dark:text-indigo-300 font-mono flex items-center gap-1">
                                                    <Users size={11} className="text-indigo-500 dark:text-indigo-400 shrink-0" />
                                                    <span className="truncate" title={String(groupDn)}>{groupCn}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
}
